// src/admin/components/AdminTable.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_SANS } from '../../constants/theme.js';

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontFamily: FONT_SANS,
  fontSize: '13px',
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: `2px solid ${COLORS.border}`,
  color: COLORS.textSecondary,
  fontWeight: 600,
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: `1px solid ${COLORS.border}`,
  color: COLORS.ink,
};

// Zebra + hover tints derived from the accent/field tokens rather than raw black
// and the old ESC red, so rows read as part of the same surface family as cards.
const ZEBRA_BG = COLORS.field;
const HOVER_BG = COLORS.accentSoft;

export default function AdminTable({ columns, rows, loading, emptyMessage, onRowClick, getRowKey }) {
  const { t } = useTranslation('admin');
  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT_SANS }}>
        {t('shared.loading')}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT_SANS }}>
        {emptyMessage ?? t('shared.emptyResult')}
      </div>
    );
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {columns.map(col => (
            <th key={col.key} style={{ ...thStyle, ...(col.width ? { width: col.width } : {}) }}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const key = getRowKey ? getRowKey(row, i) : (row.user_id || row.id || i);
          return (
            <tr
              key={key}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                background: i % 2 === 0 ? 'transparent' : ZEBRA_BG,
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = HOVER_BG; }}
              onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : ZEBRA_BG; }}
            >
              {columns.map(col => (
                <td key={col.key} style={tdStyle}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
